import { RouteLoaderData } from '@/app/routes/__root'
import MapAdvanced from '@/components/features/maps/MapAdvanced'
import type { RasterLayerAsset } from '@/components/features/maps/MapAdvanced/hooks'
import NotFound from '@/components/utils/NotFound'
import RouteLoadFailed from '@/components/utils/RouteLoadFailed'
import { useAuth } from '@/context/AuthContext'
import { throwLoaderError } from '@/lib/loader-errors'
import { collectRasterLayers } from '@/lib/rasterLayers'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { z } from 'zod'

import { Spinner } from '@droidmap/frontend-ui'
import { DroidMapMetadata } from '@droidmap/shared-service-contract'

/**
 * `?asset=<id>` names the raster the map was opened on — every click-through
 * from an asset grid/table carries it — so the map lands showing that layer
 * rather than the set's default first raster. Optional: the route is also
 * reached from the asset set's "Advanced map" button, which opens the set as a
 * whole.
 */
const mapAdvancedSearchSchema = z.object({
  asset: z.string().optional(),
})

export const Route = createFileRoute(
  '/_authenticated/orgs/$orgId_/projects/$projectId_/_pathless/asset-sets/$assetSetId_/map-advanced',
)({
  validateSearch: mapAdvancedSearchSchema,
  loader: async ({
    params,
    context,
  }): Promise<
    RouteLoaderData & {
      assetSetId: string
      layers: RasterLayerAsset[]
    }
  > => {
    const { assetSetId, orgId, projectId } = params
    const { assetClient } = context.client
    const { metadata } = context
    try {
      const assetSet = await assetClient.getAssetSet({ assetSetId }, metadata)

      // Collect every georeferenced raster/COG asset in the set — these are the
      // toggleable layers the advanced map's panel drives. Plain `.tif` texture
      // maps (no CRS) are excluded: their cog_info 500s the tile service.
      const layers = await collectRasterLayers(
        assetClient,
        assetSetId,
        metadata,
      )

      return {
        crumb: 'advanced map',
        precedingCrumbs: [
          {
            label: 'Asset Sets',
            to: '/orgs/$orgId/projects/$projectId/asset-sets',
            params: { orgId, projectId },
          },
          {
            label: assetSet.name,
            to: '/orgs/$orgId/projects/$projectId/asset-sets/$assetSetId',
            params: { orgId, projectId, assetSetId },
          },
        ],
        assetSetId,
        layers,
        metadata,
        layout: {
          variant: 'workspace',
        },
      }
    } catch (error) {
      // See #2658: a failed load is not a missing asset set. Only a 404/403
      // reaches the not-found screen; the rest get a retryable error.
      throwLoaderError(error, 'asset set map layers')
    }
  },
  component: MapAdvancedRoute,
  notFoundComponent: () => (
    <NotFound
      resource="Asset Set"
      message="This asset set doesn't exist or couldn't be loaded."
    />
  ),
  errorComponent: () => <RouteLoadFailed resource="map" />,
})

function MapAdvancedRoute() {
  const auth = useAuth()
  const router = useRouter()
  const { assetSetId, layers, metadata } = Route.useLoaderData()
  const { asset: focusLayerId } = Route.useSearch()

  // Warm the access token before mounting the map (mirrors the simple map
  // route); the tile/basemap token hooks inside the map depend on it.
  const authQuery = useQuery({
    queryKey: ['authAccessToken', auth],
    queryFn: () => auth.getAccessToken(),
  })

  if (authQuery.isLoading) {
    return <Spinner size="page" className="text-accent" />
  }
  if (authQuery.isError) {
    return <div>Error {authQuery.error.message}</div>
  }
  if (!authQuery.data) {
    return <div>No access token available</div>
  }

  // Note: an asset set with no rasters still renders — the advanced map's
  // Annotate tool works basemap-only (no raster/DEM required), so MapAdvanced
  // handles an empty layer list by framing to a default world view.
  return (
    <MapAdvanced
      layers={layers}
      focusLayerId={focusLayerId}
      metadata={metadata as DroidMapMetadata}
      assetSetId={assetSetId}
      // Re-run the loader so a newly-derived product (e.g. the CHM) is picked up
      // as a raster layer once the asset service confirms its upload.
      onProductCreated={() => router.invalidate()}
    />
  )
}
